import { useEffect, useState } from "react";
import { useClock, fmtTime } from "../lib/clockFormat";
import { useNavigate } from "react-router-dom";
import { Clock, LogOut, X, Check, Trash2, Printer, BookOpen, PencilRuler } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PALETTE = ["#FCA5A5", "#86EFAC", "#FDE047", "#D8B4FE", "#7DD3FC", "#A7E4D3", "#FDBA74", "#C7D2FE"];
const TYPE_STYLE = { break: "#FDE68A88", lunch: "#F9A8D488", free: "#C7D2FE88" };

export default function TeacherHome() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const h24 = useClock();
  const [ownSlots, setOwnSlots] = useState({});
  const [week, setWeek] = useState("A");
  const [editing, setEditing] = useState(null); // {day, idx, key}
  const [allocText, setAllocText] = useState("");
  const [hwText, setHwText] = useState("");
  const [hwSubGroup, setHwSubGroup] = useState("");
  const [hwDue, setHwDue] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/teacher/bootstrap");
      setData(data);
      setOwnSlots(data.own_slots || {});
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  if (!data) return <div className="min-h-screen flex items-center justify-center font-display font-black text-2xl animate-pulse" data-testid="teacher-loading">Loading your schedule…</div>;

  const { days, slots } = data.school.schedule;
  const weeks = data.school.schedule.weeks || 1;
  const slotKey = (day, idx) => (weeks === 2 ? `${week}:${day}:${idx}` : `${day}:${idx}`);
  const dayOverrides = data.school.schedule.day_overrides || {};
  const daySlotsList = days.map((d) => dayOverrides[d]?.slots || slots);
  const rowCount = Math.max(slots.length, ...daySlotsList.map((s) => s.length));
  const slotFor = (day, idx) => (dayOverrides[day]?.slots || slots)[idx];
  const subjectName = (id) => data.subjects.find((s) => s.id === id)?.name || "Class";

  const subjectColor = (id) => {
    const i = data.subjects.findIndex((s) => s.id === id);
    return PALETTE[(i < 0 ? 0 : i) % PALETTE.length];
  };

  const openSlot = (day, idx) => {
    const key = slotKey(day, idx);
    setEditing({ day, idx, key });
    setAllocText(ownSlots[key] || "");
    setHwText("");
    setHwSubGroup("");
    setHwDue("");
  };

  const saveAlloc = async () => {
    const next = { ...ownSlots };
    if (allocText.trim()) next[editing.key] = allocText.trim();
    else delete next[editing.key];
    setOwnSlots(next);
    try {
      await api.put("/teacher/slots", { entries: next });
      toast.success(allocText.trim() ? "Slot allocated" : "Slot cleared");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const clearAlloc = async () => {
    const next = { ...ownSlots };
    delete next[editing.key];
    setOwnSlots(next);
    setAllocText("");
    setEditing(null);
    try {
      await api.put("/teacher/slots", { entries: next });
      toast.success("Slot cleared");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sendHomework = async () => {
    if (!hwText.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post("/teacher/homework", {
        key: editing.key, text: hwText.trim(),
        ...(hwSubGroup ? { sub_group: hwSubGroup } : {}),
        ...(hwDue ? { due_date: hwDue } : {}),
      });
      toast.success(`Homework sent to ${data.pupils_notified} pupil(s) — due ${new Date(data.due_date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`);
      setHwText("");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
    }
  };

  const deleteHomework = async (id) => {
    try {
      await api.delete(`/teacher/homework/${id}`);
      toast.success("Homework removed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const derivedFor = editing ? data.derived[editing.key] : null;

  return (
    <div className="min-h-screen" data-testid="teacher-home">
      <header className="border-b-2 border-zinc-900 bg-[#7DD3FC] sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 shrink-0 rounded-xl border-2 border-zinc-900 bg-white flex items-center justify-center shadow-[3px_3px_0px_0px_#18181B]">
              <Clock className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="font-display font-black text-lg leading-tight truncate" data-testid="teacher-greeting">{user.name}</div>
              <div className="text-xs font-bold text-zinc-700 truncate">{user.school_name} · Teacher</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0 no-print">
            <button onClick={() => window.print()} data-testid="teacher-print-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <Printer className="w-4 h-4" /> <span className="hidden sm:inline">Print</span>
            </button>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="teacher-logout-btn" className="nb-btn bg-white px-3 py-2 text-sm">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="hidden print:block font-display font-black text-xl mb-3">{user.name} — {user.school_name}{weeks === 2 ? ` · Week ${week}` : ""}</div>
        {weeks === 2 && (
          <div className="flex gap-2 mb-4 no-print" data-testid="teacher-week-tabs">
            {["A", "B"].map((w) => (
              <button key={w} onClick={() => setWeek(w)} data-testid={`teacher-week-tab-${w}`}
                className={`nb-btn px-5 py-2 text-sm ${week === w ? "bg-[#1E3A8A] text-white" : "bg-white text-zinc-600"}`}>
                Week {w}
              </button>
            ))}
          </div>
        )}

        <div className="nb-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[720px]" data-testid="teacher-timetable">
              <thead>
                <tr>
                  <th className="border-b-2 border-r-2 border-zinc-900 bg-zinc-900 text-white text-xs font-bold p-3 text-left w-28 sticky left-0">Time</th>
                  {days.map((d) => (
                    <th key={d} className="border-b-2 border-zinc-900 bg-zinc-900 text-white p-3 font-bold text-sm">{d}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rowCount }).map((_, idx) => {
                  const slot = slots[idx];
                  return (
                  <tr key={idx}>
                    <td className="border-r-2 border-b-2 border-zinc-900 bg-white p-3 sticky left-0 z-10">
                      <div className="font-bold text-sm">{slot ? slot.label : "—"}</div>
                      <div className="text-xs text-zinc-500 font-semibold">{slot ? `${fmtTime(slot.start, h24)}–${fmtTime(slot.end, h24)}` : ""}</div>
                    </td>
                    {days.map((day, di) => {
                      const dSlot = daySlotsList[di][idx];
                      if (!dSlot) {
                        return <td key={day} className="border-b-2 border-zinc-300 p-1.5 bg-zinc-100" data-testid={`teacher-slot-${day.toLowerCase()}-${idx}`} />;
                      }
                      if (dSlot.type !== "period") {
                        return (
                          <td key={day} className="border-b-2 border-zinc-300 p-2 text-center text-xs font-bold text-zinc-500"
                            style={{ backgroundColor: TYPE_STYLE[dSlot.type] }} data-testid={`teacher-slot-${day.toLowerCase()}-${idx}`}>
                            {dSlot.label}
                          </td>
                        );
                      }
                      const key = slotKey(day, idx);
                      const derived = data.derived[key];
                      const own = ownSlots[key];
                      return (
                        <td key={day} className="border-b-2 border-zinc-300 p-1.5">
                          <button onClick={() => openSlot(day, idx)} data-testid={`teacher-slot-btn-${day.toLowerCase()}-${idx}`}
                            className={`w-full min-h-[64px] rounded-lg border-2 p-2 text-left transition-[transform,box-shadow] duration-150 hover:scale-[1.02] ${derived || own ? "border-zinc-900 shadow-[2px_2px_0px_0px_#18181B]" : "border-dashed border-zinc-300 bg-zinc-50 hover:border-zinc-900 hover:bg-white"}`}
                            style={derived ? { backgroundColor: subjectColor(derived.subject_id) } : own ? { backgroundColor: "#E4E4E7" } : {}}>
                            {dayOverrides[day] && <div className="text-[9px] font-black text-zinc-400 mb-0.5">{dSlot.label} · {fmtTime(dSlot.start, h24)}–{fmtTime(dSlot.end, h24)}</div>}
                            {derived ? (
                              <>
                                <div className="font-display font-bold text-sm leading-tight">{subjectName(derived.subject_id)}</div>
                                <div className="text-[11px] font-semibold text-zinc-700 mt-0.5 leading-tight">
                                  {derived.year_groups.join(", ")} · {derived.pupil_count} pupil{derived.pupil_count > 1 ? "s" : ""}
                                </div>
                                {own && <div className="text-[11px] font-bold text-zinc-600 mt-0.5 italic">{own}</div>}
                              </>
                            ) : own ? (
                              <div className="font-bold text-sm leading-tight text-zinc-700">{own}</div>
                            ) : (
                              <span className="text-xs font-bold text-zinc-400">Free</span>
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <p className="text-xs text-zinc-500 font-semibold mt-3">
          Your classes appear automatically when pupils add you to their timetable. Tap a free slot to allocate it (marking, cover, clubs…) — tap a class to set homework for those pupils.
        </p>

        {(data.homework || []).length > 0 && (
          <div className="mt-8 no-print" data-testid="teacher-homework-list">
            <h2 className="font-display font-bold text-lg mb-3">Homework you've set</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.homework.map((h) => {
                const past = h.due_date < new Date().toISOString().slice(0, 10);
                return (
                  <div key={h.id} className={`nb-card p-4 ${past ? "opacity-60" : ""}`} data-testid={`teacher-hw-${h.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="font-display font-bold text-sm">{h.day} · {h.slot_label}{h.sub_group ? ` · ${h.sub_group}` : ""}</div>
                        <p className="text-sm text-zinc-600 mt-1 whitespace-pre-wrap">{h.text}</p>
                        <p className="text-[11px] font-bold text-zinc-400 mt-2">
                          Due {new Date(h.due_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })} · {h.pupil_ids.length} pupil(s)
                        </p>
                      </div>
                      <button onClick={() => deleteHomework(h.id)} data-testid={`teacher-hw-delete-${h.id}`}
                        className="p-2 shrink-0 rounded-lg border-2 border-zinc-900 bg-red-100 hover:bg-red-200 transition-colors duration-150">
                        <Trash2 className="w-4 h-4 text-red-700" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-50 bg-zinc-900/50 flex items-center justify-center p-4" data-testid="teacher-slot-modal">
          <div className="nb-card p-6 w-full max-w-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-1">
              <h3 className="font-display font-bold text-xl">{editing.day} · {slotFor(editing.day, editing.idx)?.label}</h3>
              <button onClick={() => setEditing(null)} data-testid="teacher-slot-close" className="p-1.5 rounded-lg border-2 border-zinc-900 bg-white hover:bg-zinc-100"><X className="w-4 h-4" /></button>
            </div>
            <p className="text-xs font-semibold text-zinc-400 mb-4">{fmtTime(slotFor(editing.day, editing.idx)?.start, h24)}–{fmtTime(slotFor(editing.day, editing.idx)?.end, h24)}{weeks === 2 ? ` · Week ${week}` : ""}</p>

            {derivedFor && (
              <div className="rounded-xl border-2 border-zinc-900 p-3 mb-4" style={{ backgroundColor: subjectColor(derivedFor.subject_id) }} data-testid="teacher-slot-class-info">
                <div className="font-display font-bold">{subjectName(derivedFor.subject_id)}</div>
                <div className="text-xs font-semibold text-zinc-700">{derivedFor.year_groups.join(", ")} · {derivedFor.pupil_count} pupil(s)</div>
              </div>
            )}

            <div>
              <label className="nb-label" htmlFor="alloc-text">Allocate this slot (marking, cover, club…)</label>
              <div className="flex gap-2 mt-2">
                <input id="alloc-text" className="nb-input flex-1" data-testid="teacher-alloc-input" placeholder="e.g. Marking Y9 books"
                  value={allocText} onChange={(e) => setAllocText(e.target.value)} maxLength={80} />
                <button onClick={saveAlloc} data-testid="teacher-alloc-save" className="nb-btn bg-[#7DD3FC] px-4 py-2 text-sm shrink-0"><Check className="w-4 h-4" /></button>
                {ownSlots[editing.key] && (
                  <button onClick={clearAlloc} data-testid="teacher-alloc-clear"
                    className="nb-btn bg-red-100 px-3 py-2 text-red-700 shrink-0" type="button"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            </div>

            {derivedFor && (
              <div className="mt-5 pt-5 border-t-2 border-zinc-100">
                <label className="nb-label flex items-center gap-1.5" htmlFor="hw-text"><BookOpen className="w-3.5 h-3.5" /> Set homework for this class</label>
                {(derivedFor.sub_groups || []).length > 0 && (
                  <div className="mt-2">
                    <span className="nb-label">Only send to a sub-group (optional)</span>
                    <div className="mt-1.5 flex flex-wrap gap-2">
                      {derivedFor.sub_groups.map((g) => (
                        <button key={g} type="button" data-testid={`hw-subgroup-${g}`}
                          onClick={() => setHwSubGroup(hwSubGroup === g ? "" : g)}
                          className={`px-3 py-1.5 rounded-xl border-2 border-zinc-900 text-xs font-bold transition-[transform,box-shadow,background-color] duration-150 ${hwSubGroup === g ? "bg-[#D8B4FE] shadow-[2px_2px_0px_0px_#18181B]" : "bg-white text-zinc-500"}`}>
                          {g}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <textarea id="hw-text" className="nb-input mt-2 min-h-[90px] resize-y" data-testid="teacher-homework-input" maxLength={2000}
                  placeholder="e.g. Finish worksheet 4B — due Thursday" value={hwText} onChange={(e) => setHwText(e.target.value)} />
                <div className="mt-3">
                  <label className="nb-label" htmlFor="hw-due">Due date (blank = next {editing.day} lesson, skipping holidays)</label>
                  <input id="hw-due" type="date" className="nb-input mt-2" data-testid="teacher-homework-due" value={hwDue} onChange={(e) => setHwDue(e.target.value)} />
                </div>
                <button onClick={sendHomework} disabled={sending || !hwText.trim()} data-testid="teacher-homework-send"
                  className="nb-btn w-full bg-[#1E3A8A] text-white py-2.5 mt-3">
                  <PencilRuler className="w-4 h-4" /> {sending ? "Sending…" : `Send to ${derivedFor.pupil_count} pupil(s)`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
